// components/integrations/RecruiterFlowImport.jsx
// Import candidates from RecruiterFlow into SurFox campaigns

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Download, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  X,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import supabase from '../../../lib/supabaseClient';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function RecruiterFlowImport({ isOpen, onClose, onImportComplete }) {
  const { user } = useAuth();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(true);
  
  // Filter options (from RecruiterFlow)
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Selected filters
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [nameSearch, setNameSearch] = useState('');
  
  // Candidates
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);
  
  // SurFox campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (isOpen && user?.tenant_id) {
      checkConnection();
      fetchCampaigns();
    }
  }, [isOpen, user?.tenant_id]);

  useEffect(() => {
    if (isConnected) {
      fetchFilterOptions();
    }
  }, [isConnected]);

  // ============================================================================
  // API CALLS
  // ============================================================================

  const callRecruiterFlowProxy = async (action, params = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/recruiterflow-proxy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          action,
          tenant_id: user.tenant_id,
          params
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return await response.json();
  };

  const checkConnection = async () => {
    setConnectionLoading(true);
    try {
      const result = await callRecruiterFlowProxy('test_connection');
      setIsConnected(result.success);
      if (!result.success) {
        setError('RecruiterFlow is not connected. Please add your API key in Settings → Integrations.');
      }
    } catch (err) {
      setIsConnected(false);
      setError('RecruiterFlow is not connected. Please add your API key in Settings → Integrations.');
    } finally {
      setConnectionLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Fetch jobs, stages, and tags in parallel
      const [jobsResult, stagesResult, tagsResult] = await Promise.all([
        callRecruiterFlowProxy('get_jobs', { only_open: '1' }),
        callRecruiterFlowProxy('get_stages'),
        callRecruiterFlowProxy('get_tags')
      ]);

      setJobs(jobsResult.data || []);
      setStages(stagesResult.data || []);
      setTags(tagsResult.data || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setError('Failed to load filter options from RecruiterFlow');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, status')
        .eq('tenant_id', user.tenant_id)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const searchCandidates = async () => {
    setCandidatesLoading(true);
    setError('');
    setCandidates([]);
    setSelectedCandidates([]);

    try {
      const params = {
        items_per_page: 100
      };

      if (selectedJob) params.job_id = parseInt(selectedJob);
      if (selectedStage) params.stage = selectedStage;
      if (selectedTags.length > 0) params.tags = selectedTags.map(t => parseInt(t));
      if (nameSearch.trim()) params.name = nameSearch.trim();

      const result = await callRecruiterFlowProxy('search_candidates', params);
      
      setCandidates(result.data || []);
      setTotalCandidates(result.total_items || 0);
      
      if (result.data?.length === 0) {
        setError('No candidates found matching your filters');
      }
    } catch (err) {
      console.error('Error searching candidates:', err);
      setError('Failed to search candidates: ' + err.message);
    } finally {
      setCandidatesLoading(false);
    }
  };

  // ============================================================================
  // IMPORT LOGIC
  // ============================================================================

  const handleImport = async () => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    if (!selectedCampaign) {
      setError('Please select a campaign');
      return;
    }

    setImporting(true);
    setError('');
    setImportResult(null);

    try {
      const candidatesToImport = candidates.filter(c => 
        selectedCandidates.includes(c.id)
      );

      let imported = 0;
      let skipped = 0;
      let errors = 0;

      for (const candidate of candidatesToImport) {
        try {
          // Check if lead already exists by phone
          const phone = candidate.phone_number?.[0] || '';
          const phoneDigits = phone.replace(/\D/g, '');

          if (phoneDigits) {
            const { data: existing } = await supabase
              .from('leads_decrypted')
              .select('id')
              .eq('tenant_id', user.tenant_id)
              .eq('phone', phoneDigits)
              .single();

            if (existing) {
              skipped++;
              continue;
            }
          }

          // Create lead
          const { error: insertError } = await supabase
            .from('leads')
            .insert({
              tenant_id: user.tenant_id,
              campaign_id: selectedCampaign,
              name: candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
              first_name: candidate.first_name || null,
              last_name: candidate.last_name || null,
              phone: phoneDigits || null,
              email: candidate.email?.[0]?.email || null,
              status: 'New',
              source: 'RecruiterFlow Import',
              external_id: `rf_${candidate.id}`,
              metadata: {
                recruiterflow_id: candidate.id,
                recruiterflow_source: candidate.source_name,
                imported_at: new Date().toISOString()
              }
            });

          if (insertError) {
            console.error('Error inserting lead:', insertError);
            errors++;
          } else {
            imported++;
          }
        } catch (err) {
          console.error('Error importing candidate:', err);
          errors++;
        }
      }

      setImportResult({
        total: candidatesToImport.length,
        imported,
        skipped,
        errors
      });

      if (imported > 0) {
        onImportComplete?.();
      }
    } catch (err) {
      console.error('Import error:', err);
      setError('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  // ============================================================================
  // SELECTION HELPERS
  // ============================================================================

  const toggleSelectAll = () => {
    if (selectedCandidates.length === candidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(candidates.map(c => c.id));
    }
  };

  const toggleCandidate = (id) => {
    setSelectedCandidates(prev => 
      prev.includes(id) 
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Import from RecruiterFlow</h3>
              <p className="text-sm text-slate-500">Search and import candidates into a SurFox campaign</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Connection Loading */}
          {connectionLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          )}

          {/* Not Connected */}
          {!connectionLoading && !isConnected && (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">RecruiterFlow Not Connected</p>
              <p className="text-sm text-slate-500 mt-2">
                Add your RecruiterFlow API key in Settings → Integrations to import candidates.
              </p>
            </div>
          )}

          {/* Connected - Show Filters & Results */}
          {!connectionLoading && isConnected && (
            <div className="space-y-6">
              
              {/* Filters */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                <div className="flex items-center space-x-2 text-sm font-medium text-slate-700">
                  <Filter className="w-4 h-4" />
                  <span>Filter Candidates</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Job Filter */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Job</label>
                    <select
                      value={selectedJob}
                      onChange={(e) => setSelectedJob(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="">All Jobs</option>
                      {jobs.map(job => (
                        <option key={job.id} value={job.id}>
                          {job.name || job.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Stage Filter */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Stage</label>
                    <select
                      value={selectedStage}
                      onChange={(e) => setSelectedStage(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="">All Stages</option>
                      {stages.map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tags Filter */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tags</label>
                    <select
                      value={selectedTags[0] || ''}
                      onChange={(e) => setSelectedTags(e.target.value ? [e.target.value] : [])}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="">All Tags</option>
                      {tags.map(tag => (
                        <option key={tag.id} value={tag.id}>{tag.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Name Search */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={nameSearch}
                      onChange={(e) => setNameSearch(e.target.value)}
                      placeholder="Search by name..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                {/* Search Button */}
                <div className="flex justify-end">
                  <button
                    onClick={searchCandidates}
                    disabled={candidatesLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
                  >
                    {candidatesLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span>Search RecruiterFlow</span>
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Results */}
              {candidates.length > 0 && (
                <div className="space-y-4">
                  {/* Results Header */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Found <span className="font-semibold">{totalCandidates}</span> candidates
                      {selectedCandidates.length > 0 && (
                        <span className="ml-2 text-sky-600">
                          ({selectedCandidates.length} selected)
                        </span>
                      )}
                    </div>
                    <button
                      onClick={toggleSelectAll}
                      className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                    >
                      {selectedCandidates.length === candidates.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {/* Candidates List */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      {candidates.map(candidate => (
                        <label
                          key={candidate.id}
                          className={`flex items-center p-3 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 ${
                            selectedCandidates.includes(candidate.id) ? 'bg-sky-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCandidates.includes(candidate.id)}
                            onChange={() => toggleCandidate(candidate.id)}
                            className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 mr-3"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown'}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {candidate.phone_number?.[0] || 'No phone'}
                              {candidate.email?.[0]?.email && ` • ${candidate.email[0].email}`}
                            </p>
                          </div>
                          {candidate.jobs?.[0] && (
                            <span className="ml-2 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full truncate max-w-32">
                              {candidate.jobs[0].name}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Campaign Selection */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Import to Campaign
                    </label>
                    <select
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="">Select a campaign...</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Import Complete</span>
                  </div>
                  <div className="text-sm text-emerald-800 space-y-1">
                    <p>✓ {importResult.imported} candidates imported</p>
                    {importResult.skipped > 0 && (
                      <p>○ {importResult.skipped} skipped (already exist)</p>
                    )}
                    {importResult.errors > 0 && (
                      <p>✗ {importResult.errors} failed</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {importResult ? 'Close' : 'Cancel'}
          </button>
          {isConnected && candidates.length > 0 && !importResult && (
            <button
              onClick={handleImport}
              disabled={importing || selectedCandidates.length === 0 || !selectedCampaign}
              className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>
                {importing 
                  ? 'Importing...' 
                  : `Import ${selectedCandidates.length} Candidate${selectedCandidates.length !== 1 ? 's' : ''}`
                }
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}